import L from "leaflet";
import "leaflet/dist/leaflet.css";
import type { Polygon } from "geojson";
import mustache from "mustache";
import { GOOGLE_MAPS_API_KEY } from "../../../../../../constantes";
import {
  departamentoGrpcCatalogService,
  StoredDepartamento,
} from "../../../../../modulos/catalogos/terceros/services/DepartamentoGrpcCatalogService";
// Piloto real de gRPC-Web en vivo (ver CLAUDE.md del Server): municipios por
// departamentoId bajo demanda, en vez de precargar las de los 33 departamentos
// del pais de una sola vez: pesan ~75 MB sin simplificar.
import {
  municipioByDepartamentoIdGrpcService,
  StoredMunicipio,
} from "../../../../../modulos/catalogos/terceros/services/MunicipioByDepartamentoIdGrpcService";
import { IConnectRender } from "../../../../definitions/IConnectRender";
import { fx_filaAvisos } from "./mapAvisos";
import { fx_acotarMapaA } from "../../../../implementations/map/mapLimites";
import { MapZoomButton } from "../../../../implementations/map/MapZoomButton";
import { AoiHerramienta, ERROR_TIMEOUT_MS } from "./AoiDrawEngine";
import { AoiDrawToolbar } from "./AoiDrawToolbar";
import { AoiSearchBarRender } from "./AoiSearchBarRender";
import { AoiWmsLayer } from "./AoiWmsLayer";
import { PoligonosPadreWfsService } from "./PoligonosPadreWfsService";
import type { NotificacionPiezas } from "./AoiDrawToolbar";
import type { OperacionVerticeAoi } from "./AoiSnapEditEngine";
import { fx_tolerarClickAlArrastrar } from "./clickTolerancia";
import template from "./aoiMapTemplate.html";

mustache.parse(template);

let AOI_MAP_ID = 0;

/** Pane dedicado a los contornos de departamento — necesario para el fade-in
 *  inicial: con `preferCanvas: true` los polígonos no tienen un `<path>` DOM
 *  propio al que animarle `opacity`, pero el pane sí es un `<div>` real. */
const DEPARTAMENTOS_PANE = "aoiDepartamentosPane";

/** Contornos de referencia (departamento y municipio): amarillo, ESTABLE, sin
 *  ningún cambio de color por estado — pedido explícito. Antes el
 *  departamento era naranja y pasaba a amarillo al elegirlo, y ambos se ponían
 *  blancos mientras se dibujaba; eso se eliminó: son solo referencia visual,
 *  el usuario no interactúa con ellos y el parpadeo de colores distraía del
 *  AOI que está trazando. Única diferencia entre los dos, el grosor. */
const DEPARTAMENTO_STYLE: L.PathOptions = {
  color: "yellow",
  weight: 1,
  opacity: 0.6,
  fillOpacity: 0,
};

const MUNICIPIO_STYLE: L.PathOptions = {
  color: "yellow",
  weight: 1.5,
  opacity: 0.85,
  fillOpacity: 0,
};

/** Aviso de ayuda que se muestra centrado arriba del mapa segun la
 *  herramienta activa — mismo recurso que la app de referencia externa
 *  (`BarraDibujo.tsx`, constante `AVISOS`), que tambien reusa el card de
 *  notificaciones de su sistema en vez de inventar uno. Ocupa justo el hueco
 *  que deja la barra de busqueda al ocultarse.
 *
 *  No viaja solo: al lado suyo, en la MISMA fila, se pinta el card con el
 *  nombre del paso (`_avisoPaso`, pedido explicito) — el usuario ve de un
 *  vistazo que esta haciendo ("1) Dibuje el area global del predio.") y como
 *  hacerlo ("De Click en el mapa para dibujar."). */
/** Aviso de error cuando el motor rechaza algo por pisar otra pieza (un
 *  vértice dentro de ella, un tramo que la atraviesa, una forma que la solapa
 *  al cerrarse o al editarla). Acompaña al parpadeo rojo que ya hace el motor
 *  sobre la pieza invadida: el rojo muestra QUÉ se pisó, esto dice POR QUÉ no
 *  se puede. Se borra solo a los `ERROR_TIMEOUT_MS`, igual que ese parpadeo.
 *
 *  A diferencia del nombre del paso (`_avisoPaso`, que pone el wizard), este
 *  texto vive acá: es una regla del propio mapa —las piezas del AOI no se
 *  pueden solapar, porque rompen la unión topológica (`AoiPolygonUnion`)—, no
 *  algo del paso que lo monta. Mismo criterio que `AVISOS`. */
const AVISO_SOLAPE = "El perímetro de los lotes no se pueden cruzar o tocar.";

/** Ícono del card con el nombre del paso 3: el mismo `ph-polygon` del botón
 *  "Dibujar" de la toolbar, así el aviso y la herramienta se leen como lo
 *  mismo. */
const ICONO_PASO = "ph-polygon";

/** Cuánto movimiento del mouse sigue contando como CLICK y no como arrastre,
 *  en píxeles Manhattan (|dx| + |dy|). Leaflet trae 3 y con eso se perdían del
 *  orden de 2 de cada 10 clicks al dibujar. 6 da ~3 px por eje en diagonal, que
 *  es lo que se mueve la mano apoyada, sin llegar a estorbar el paneo. */
const TOLERANCIA_CLICK_PX = 6;

const AVISOS: Record<AoiHerramienta, string> = {
  draw: "De Click en el mapa para dibujar.",
  edit: "De click en un lote para editar su forma.",
  delete: "De click sobre un lote para eliminarlo.",
};

/** Aviso del modo Editar cuando YA hay una pieza en edición.
 *
 *  Son dos avisos porque son dos momentos: elegir la pieza y editarla. Con una
 *  sola pieza el primero no existe —`AoiDrawToolbar` entra directo a sus
 *  vértices— y el usuario ve este desde el arranque; con dos o más ve el de
 *  `AVISOS.edit` hasta que clickea una. */
const AVISO_EDITAR_VERTICES =
  "Arrastre un vértice para moverlo, el intermedio para agregarlo, o haga clic en uno para quitarlo.";

/** Mapa de dibujo del AOI (paso 3 del wizard de registro de finca). A
 *  diferencia de `BaseMapRender`, no asume que la finca ya tiene
 *  ubicación/lotes: arranca centrado en Colombia hasta que el usuario
 *  busque una ubicación (link de Google Maps, o Departamento/Región —
 *  ambos en `AoiSearchBarRender`, overlay flotante) o navegue manualmente.
 *  Dibuja los contornos de los departamentos (catálogo DANE) como referencia
 *  visual; al elegir uno desde el buscador (`selectDepartamento`, vía
 *  callback) el mapa centra y hace zoom a su extent y dibuja las municipios de
 *  ese departamento
 *  — sin restringir el paneo: `maxBounds`/`minZoom` quedan fijos a nivel país
 *  todo el tiempo (seteados una sola vez en `_drawDepartamentos`), el usuario
 *  siempre puede alejarse a ver todo el país. Todos los movimientos del mapa
 *  (país↔departamento) van animados. Fases pendientes: toolbar de dibujo,
 *  validación, unión topológica y guardado. */
export class AoiMapRender implements IConnectRender {
  private readonly _mapId: string;
  private _map: L.Map | null = null;
  private _wmsPadres: AoiWmsLayer | null = null;
  /** Lectura de la geometría de una pieza, el camino degradado del modo Editar
   *  (la fuente normal son sus vértices). Se arma junto con la capa WMS: las
   *  dos necesitan el registro. */
  private _padresWfs: PoligonosPadreWfsService | null = null;
  private _searchBar = new AoiSearchBarRender();
  /** Contorno de cada departamento por id. Antes guardaba también el
   *  `StoredDepartamento` entero, pero eso solo servía para re-vincular el
   *  nombre del tooltip al deseleccionar — sin tooltips quedaba reteniendo la
   *  geometría completa de los 33 departamentos sin que nadie la leyera. */
  private _departamentoLayers = new Map<string, L.GeoJSON>();
  private _municipioLayerGroup = L.layerGroup();
  private _selectedDepartamentoId: string | null = null;
  /** Límite de paneo a nivel país, que `fx_acotarMapaA` ya dejó aplicado en el
   *  mapa. Se guarda solo para poder VOLVER a la vista país al deseleccionar un
   *  departamento (`_selectDepartamento(null)`); los límites en sí no se vuelven
   *  a tocar nunca. */
  private _paisMaxBounds: L.LatLngBounds | null = null;
  private _zoomIn = new MapZoomButton("ph-plus", "Acercar", (m) => m.zoomIn());
  private _zoomOut = new MapZoomButton("ph-minus", "Alejar", (m) => m.zoomOut());
  private _drawToolbar = new AoiDrawToolbar();

  /** @param _existingPolygons Piezas ya guardadas para restaurar (lotes de
   *  la finca si ya los tiene, o el AOI completo como una sola pieza si
   *  todavía no llegó a paso 4 — la decisión de cuál usar la toma quien
   *  construye este render, ver `RegistroFincaPaso3Render.connectBody`).
   *  Array vacío = primera vez, arranca en blanco. Si viene, se carga como
   *  capas dibujadas en `connect()` y el mapa encuadra ahí en vez de
   *  arrancar centrado en Colombia — reingresar a este paso (ej. "Volver"
   *  desde el paso 4) debe mostrar lo que ya se dibujó, no arrancar en
   *  blanco.
   *  @param _mostrarBusquedaYDepartamentos `false` cuando el AOI vino de un
   *   KML/KMZ importado (`borrador.aoiOrigen === "kml"`, ver
   *   `RegistroFincaPaso3Render`) — el usuario ya trajo su geometría exacta,
   *   no necesita buscador ni contornos de departamento/región de
   *   referencia. En ese caso el mapa se acota a la extensión de
   *   `_existingPolygons` (con margen) en vez de a los límites del país.
   *  @param _avisoPaso Nombre del paso del wizard, que se pinta como un
   *   segundo card a la IZQUIERDA del aviso de la herramienta, en la misma
   *   fila (ver `AVISOS`). El texto lo pone quien construye este render
   *   (`RegistroFincaPaso3Render`) y no este componente: el mapa no sabe en
   *   qué paso de qué wizard está montado. `null` = solo el aviso de la
   *   herramienta, como antes. */
  constructor(
    private readonly _existingPolygons: Polygon[],
    private readonly _mostrarBusquedaYDepartamentos: boolean,
    private readonly _avisoPaso: string | null = null,
    /** Registro temporal en curso. Es lo único que la capa WMS de polígonos
     *  padre necesita: la Api filtra por él y valida que sea del cliente. Sin
     *  él la capa no se monta. */
    private readonly _registroId: string | null = null,
    /** Si `_existingPolygons` ya está en `poligonos_padre` —y entonces lo pinta
     *  la capa WMS— o si todavía no, y lo tiene que pintar Leaflet hasta el
     *  primer guardado.
     *
     *  `true` para las piezas restauradas del borrador; `false` para un KML
     *  recién importado en el paso 2 y para las piezas reconstruidas de los
     *  lotes (`fx_piezasDesdeLotes`), que se arman en el cliente. Ver
     *  `AoiDrawEngine.cargarExistentes`. */
    private readonly _piezasEnLaBase: boolean = true,
    /** El id de cada pieza de `_existingPolygons` en
     *  `tbl_temp_poligonos_padre`, en la misma posición (ver `AoiPieza.id`).
     *  Vacío cuando las piezas no están guardadas (KML, reconstruidas de los
     *  lotes) o cuando el registro es anterior a esa tabla. */
    private readonly _poligonosPadreIds: readonly string[] = [],
  ) {
    AOI_MAP_ID += 1;
    this._mapId = AOI_MAP_ID.toString();
  }

  render(): string {
    return mustache.render(template, { mapId: this._mapId });
  }

  /** Notifica cada vez que cambia si hay o no al menos un polígono dibujado
   *  (ver `AoiDrawToolbar.setOnAvailabilityChange`). Llamar antes de
   *  `connect()` para no perderse el primer disparo (arranca en `false`). */
  setOnDrawAvailabilityChange(handler: (hasShapes: boolean) => void): void {
    // NO se registra directo en la toolbar: este render necesita ese mismo
    // aviso para decidir si muestra el buscador, y la toolbar guarda UN solo
    // handler — registrarse los dos ahí significaba que el último pisaba al
    // otro. Se guarda acá y lo reenvía `_onDrawAvailabilityChange`.
    this._externalAvailabilityHandler = handler;
  }

  /** Notifica las piezas dibujadas en cada cambio (ver
   *  `AoiDrawToolbar.setOnPiezasChange`). Llamar antes de `connect()` por la
   *  misma razón que `setOnDrawAvailabilityChange` — si la finca ya tiene AOI
   *  restaurado, el primer disparo pasa dentro de `connect()`. */
  setOnPiezasChange(
    handler: (piezas: Polygon[], opciones?: NotificacionPiezas) => void,
  ): void {
    this._drawToolbar.setOnPiezasChange(handler);
  }

  /** Quién persiste una operación de vértice del modo Editar (ver
   *  `AoiDrawToolbar.setOnEditarVertice`). Lo cablea el paso 3, que es el que
   *  tiene el id del registro. */
  setOnEditarVertice(
    handler: (
      poligonoPadreId: string,
      operacion: OperacionVerticeAoi,
    ) => Promise<Polygon | null>,
  ): void {
    this._drawToolbar.setOnEditarVertice(handler);
  }

  /** Polígonos dibujados, uno por uno (ver
   *  `AoiDrawToolbar.getIndividualGeometries`). Es lo que se guarda: el
   *  perímetro unido lo calcula el servidor con ellos. */
  getIndividualGeometries(): Polygon[] {
    return this._drawToolbar.getIndividualGeometries();
  }

  /** Deshace la última edición de vértices porque el guardado la rechazó (ver
   *  `AoiDrawToolbar.revertirEdicionRechazada`). Lo llama el paso 3 cuando el
   *  `GuardarAoi` contesta `GEOMETRIA_INVALIDA`. */
  revertirEdicionRechazada(motivo: string): boolean {
    return this._drawToolbar.revertirEdicionRechazada(motivo);
  }

  /** Pisa las piezas con las de la base y repinta el WMS entero (ver
   *  `AoiDrawToolbar.reemplazarPiezasDeLaBase`).
   *
   *  El repintado es total y no acotado: las piezas pueden haber cambiado en
   *  cualquier parte del mapa, así que no hay un "área que cambió" que valga. */
  reemplazarPiezasDeLaBase(polygons: Polygon[], ids: readonly string[]): void {
    this._drawToolbar.reemplazarPiezasDeLaBase(polygons, ids);

    if (polygons.length > 0 && this._map) {
      const limites = L.featureGroup(
        polygons.map((polygon) => L.geoJSON(polygon)),
      ).getBounds();
      if (limites.isValid()) this._wmsPadres?.extenderPadres(limites);
    }
    this._wmsPadres?.refrescarZona(null);
  }

  /** Avisa cuando el mapa ya muestra lo que había guardado al montarse (ver
   *  `AoiWmsLayer.alPintarLoGuardado`). Lo usa el paso 3 para no destapar la
   *  pantalla antes de que las piezas estén en el mapa.
   *
   *  Hay que llamarlo DESPUÉS de `connect()`: antes no existe la capa WMS y
   *  contestaría en el acto. Sin registro —el camino "Dibujar", donde no hay
   *  nada guardado que esperar— contesta en el acto, que es lo correcto. */
  alPintarLoGuardado(alPintar: () => void): void {
    if (!this._wmsPadres) {
      alPintar();
      return;
    }
    this._wmsPadres.alPintarLoGuardado(alPintar);
  }

  /** Los polígonos dibujados con su marca de modificado (ver
   *  `AoiDrawToolbar.getPiezas`). */
  getPiezas(): { polygon: Polygon; modificada: boolean }[] {
    return this._drawToolbar.getPiezas();
  }

  /** El id de cada pieza en `tbl_temp_poligonos_padre`, en el mismo orden que
   *  `getIndividualGeometries()` — cadena vacía para una pieza que todavía no
   *  está guardada. Es lo que viaja en el `GuardarAoi` para que el servidor
   *  actualice las mismas filas (ver `AoiDrawToolbar.getPiezasIds`). */
  getPiezasIds(): string[] {
    return this._drawToolbar.getPiezasIds();
  }

  /** Adopta los ids que devolvió un guardado, SIN pedir tiles.
   *
   *  Es la mitad barata de `refrescarPoligonosPadre`: sirve cuando lo guardado
   *  no cambió lo que la capa pinta —el caso del "Continuar" del paso 3, que
   *  sólo recalcula el perímetro del lado del servidor— y pedir los tiles de
   *  nuevo se vería como un salto de la capa sin que nada haya cambiado. */
  adoptarIdsDePiezas(ids: readonly string[]): void {
    if (ids.length > 0) this._drawToolbar.adoptarIdsDePiezas(ids);
  }

  /** Vuelve a pedir los tiles del área indicada a GeoServer.
   *
   *  Lo llama el paso 3 después de guardar un polígono: recién ahí la pieza
   *  está en la base y la capa la incluye. Se pasa el área del polígono
   *  guardado y no se refresca la capa entera, que se vería como una recarga
   *  del mapa.
   *
   *  Sin `bounds` refresca todo: sirve al retomar el paso, donde cambió el
   *  registro completo. */
  refrescarPoligonosPadre(
    area: Polygon | null = null,
    /** Los ids que devolvió el guardado, para que una pieza recién dibujada
     *  quede con el suyo (ver `AoiDrawToolbar.adoptarIdsDePiezas`). */
    ids: readonly string[] = [],
  ): void {
    // ANTES de `confirmarPersistencia`: de acá en más la pieza se identifica
    // por su id, y el repintado de vértices de más abajo ya lo necesita.
    if (ids.length > 0) this._drawToolbar.adoptarIdsDePiezas(ids);

    // **El relevo entre Leaflet y GeoServer se hace al final**, cuando los
    // tiles ya están pintados (ver el cierre de este método). Acá se hacía
    // primero, con el argumento de que esperar produciría un parpadeo en
    // blanco; es al revés: soltar la copia de Leaflet antes de que llegue la
    // imagen deja la pieza sin nadie que la pinte justo durante el viaje
    // Api→GeoServer→PNG. Eso es lo que se veía como "tarda en renderizar", y
    // la primera pieza del registro era la peor porque ahí ese viaje incluye
    // la conexión HTTP nueva, el primer golpe al controlador del WMS y la
    // primera consulta de GeoServer a la capa.

    // Acá se le pedía a GeoServer que recalculara la extensión de la capa (una
    // escritura en su catálogo, con el usuario administrador desde la web). Se
    // quitó el 2026-10-11: Leaflet no usa esa extensión (cada GetMap lleva su
    // propio BBOX y lo que acota los tiles es `options.bounds`), así que era
    // solo un metadato, y la web ya no habla con GeoServer.

    // La pieza nueva agranda el área con datos: sin ampliar la extensión,
    // Leaflet no pediría los tiles donde acaba de aparecer.
    if (area) {
      const limites = L.geoJSON(area).getBounds();
      this._wmsPadres?.extenderPadres(limites);
      // Y los vértices de la pieza en edición, si hay una: el servidor los
      // re-extrajo dentro de este guardado, así que recién ahora la base tiene
      // el vértice en su posición nueva.
      this._wmsPadres?.refrescarVertices(limites);
    }

    // El área llega como GeoJSON y no como `L.LatLngBounds` a propósito: el
    // paso 3 no tiene por qué importar Leaflet sólo para calcular un
    // rectángulo. La conversión vive acá, que es donde ya está el mapa.
    //
    // Y al área que QUEDA se le suma la que una pieza borrada acaba de liberar:
    // el repintado está acotado a lo que cambió, y lo que cambió al borrar está
    // justamente FUERA del perímetro nuevo. Sin sumarla, los tiles de la pieza
    // borrada no se volvían a pedir y el polígono seguía en pantalla (ver
    // `AoiDrawToolbar.tomarZonaLiberada`).
    this._wmsPadres?.refrescarZona(
      this._zonaARepintar(area, this._drawToolbar.tomarZonaLiberada()),
    );

    // Y RECIÉN AHORA se sueltan las capas de Leaflet que venían pintando las
    // piezas mientras el guardado viajaba: con los tiles ya en pantalla el
    // relevo es invisible. Si el guardado hubiera fallado, el paso 3 no llama
    // a este método y la copia de Leaflet se queda, que es lo correcto —esa
    // pieza no está en la base, así que el WMS no la pinta.
    if (this._wmsPadres) {
      this._wmsPadres.alTerminarDePintarPadres(() =>
        this._drawToolbar.confirmarPersistencia(),
      );
    } else {
      this._drawToolbar.confirmarPersistencia();
    }
  }

  /** La zona a repintar: el área que queda, más la que liberó una pieza
   *  borrada. `null` = repintar todo, que es lo que corresponde cuando no quedó
   *  perímetro. */
  private _zonaARepintar(
    area: Polygon | null,
    liberada: [number, number, number, number] | null,
  ): L.LatLngBounds | null {
    if (!area) return null;

    const zona = L.geoJSON(area).getBounds();
    if (!liberada) return zona;

    return L.latLngBounds([zona.getSouthWest(), zona.getNorthEast()]).extend(
      L.latLngBounds(
        L.latLng(liberada[1], liberada[0]),
        L.latLng(liberada[3], liberada[2]),
      ),
    );
  }

  connect(): void {
    const container = document.getElementById(`aoiMapInstance-${this._mapId}`);
    if (!container) return;

    this._map = L.map(container, {
      center: [4.711, -74.0721],
      zoom: 6,
      maxZoom: 20,
      preferCanvas: true,
      zoomControl: false,
      // `wheelDebounceTime` queda en el default de Leaflet (40 ms).
      //
      // Estuvo en 80 y el motivo se venció: un scroll de rueda encadena 8-10
      // zooms, y cada uno reposicionaba TODOS los marcadores del trazo en
      // curso — con un polígono de 20 vértices eran cientos de
      // reposicionamientos en medio segundo. Hoy el trazo lo renderiza
      // GeoServer y de Leaflet queda un marcador, el del vértice de cierre.
      //
      // Lo que esos zooms encadenados sí seguían costando era una grilla de
      // tiles WMS por nivel; eso lo corta `updateWhenZooming: false` en
      // `SelectiveWms`, que es donde corresponde — en la capa, no frenando la
      // rueda del usuario.
    });

    // Capa WMS con los polígonos padre YA GUARDADOS de este registro. Va en su
    // propio pane, entre el satelital y los vectores del dibujo.
    if (this._registroId) {
      this._wmsPadres = new AoiWmsLayer(this._map, this._registroId);
      this._wmsPadres.addTo();
      this._padresWfs = new PoligonosPadreWfsService(this._registroId);

      // **El trazo en curso ya no viaja al servidor** (2026-10-23). Lo dibuja
      // Leaflet dentro del motor, con los vértices en memoria, y por eso acá
      // no se arma ningún `VerticesWfstService` ni se repintan tiles por
      // click. El WMS sigue siendo el único que pinta las piezas GUARDADAS.
    }

    // Dibujar es una seguidilla de clicks con la mano apoyada, y con el
    // default de Leaflet (3 px MANHATTAN) un movimiento diagonal de 2 px por
    // eje ya cuenta como arrastre: el mapa panea y el vértice no se pone.
    fx_tolerarClickAlArrastrar(this._map, TOLERANCIA_CLICK_PX);

    L.tileLayer(
      `https://mt{s}.google.com/vt?lyrs=y&x={x}&y={y}&z={z}&key=${GOOGLE_MAPS_API_KEY}`,
      { maxZoom: 20, subdomains: ["0", "1", "2", "3"] },
    ).addTo(this._map);

    // Pane de departamentos en opacity 0: se anima a 1 una sola vez, cuando
    // termina de cargar el catálogo (ver _drawDepartamentos). Los municipios
    // NO se animan (solo la carga inicial del mapa).
    const departamentosPane = this._map.createPane(DEPARTAMENTOS_PANE);
    departamentosPane.style.zIndex = "400";
    departamentosPane.style.opacity = "0";
    departamentosPane.style.transition = "opacity 600ms ease";

    this._municipioLayerGroup.addTo(this._map);

    this._avisoEl = document.getElementById(`aoiMapAviso-${this._mapId}`);

    if (this._mostrarBusquedaYDepartamentos) {
      this._searchWrapEl = document.getElementById(
        `aoiSearchBarWrap-${this._mapId}`,
      );
      if (this._searchWrapEl) {
        this._searchWrapEl.innerHTML = this._searchBar.render();
        this._searchBar.connect(this._map, (departamentoId) =>
          this._selectDepartamento(departamentoId),
        );
      }
    }

    // Orden pedido explícito: zoom+, zoom-, dibujar, editar, borrar.
    // Id con prefijo propio (NO "mapControlsRight-N", que también usa
    // BaseMapRender con su propio contador independiente): dos contadores
    // separados arrancando ambos en 1 producían el mismo id, y
    // getElementById devolvía el de un mapa de fondo (BaseMapRender de la
    // finca detrás del modal) en vez del de este mapa.
    const mapControlsRight = document.getElementById(
      `aoiMapControlsRight-${this._mapId}`,
    );
    if (mapControlsRight) {
      this._zoomIn.init(mapControlsRight, this._map);
      this._zoomOut.init(mapControlsRight, this._map);
      // Antes de `init()`: ahí adentro la toolbar hace su primer disparo de
      // disponibilidad y de herramienta activa.
      this._drawToolbar.setOnAvailabilityChange(this._onDrawAvailabilityChange);
      this._drawToolbar.setOnToolChange(this._onToolChange);
      this._drawToolbar.setOnSolapeBloqueado(this._onSolapeBloqueado);
      this._drawToolbar.init(
        mapControlsRight,
        this._map,
        // La toolbar necesita el WMS para prender los vértices de la pieza que
        // se edita y el rojo de la que se va a borrar, y la pieza por gRPC
        // como camino degradado cuando todavía no tiene vértices extraídos.
        this._wmsPadres,
        this._padresWfs,
        this._registroId,
      );

      if (this._existingPolygons.length > 0) {
        this._drawToolbar.loadExisting(
          this._existingPolygons,
          this._piezasEnLaBase,
          this._poligonosPadreIds,
        );
        const bounds = L.featureGroup(
          this._existingPolygons.map((polygon) => L.geoJSON(polygon)),
        ).getBounds();
        if (bounds.isValid()) {
          this._map.fitBounds(bounds);
          // La capa WMS nace con una extensión degenerada para no pedir tiles
          // al vacío, así que las piezas restauradas no se verían hasta el
          // primer guardado: hay que decirle dónde están los datos.
          //
          // **Es lo que faltaba al VOLVER del paso 4 al paso 3.** El mapa se
          // remonta y la capa se crea de nuevo, pero su extensión arranca en
          // cero y sólo la amplía `refrescarPoligonosPadre`, que corre después
          // de guardar. Sin guardar nada, el paso 3 quedaba con el satelital y
          // ningún polígono encima.
          if (this._piezasEnLaBase) {
            this._wmsPadres?.extenderPadres(bounds);
            this._wmsPadres?.refrescarZona(bounds);
          }

          // Modo KML: sin catálogo de departamentos que fije límites a nivel
          // país (ver más abajo), así que el mapa se acota a la extensión de
          // lo importado — el usuario ya sabe dónde está su predio, no
          // necesita panear el resto del país.
          if (!this._mostrarBusquedaYDepartamentos) {
            fx_acotarMapaA(this._map, bounds);
          }
        }
      }
    }

    // Primer pintado del aviso: el card del nombre del paso tiene que estar
    // desde que se monta el mapa. No alcanza con esperar el disparo inicial de
    // la toolbar (`_onDrawAvailabilityChange`/`_onToolChange`): si no hubiera
    // dónde montarla (`mapControlsRight` nulo) ese disparo nunca llega.
    this._refreshBuscadorYAviso();

    if (!this._mostrarBusquedaYDepartamentos) return;

    departamentoGrpcCatalogService.getListAsync().then((departamentos) => {
      if (this._disposed || !this._map) return;

      this._drawDepartamentos(departamentos);
    });
  }

  dispose(): void {
    this._disposed = true;
    // Sin esto, cerrar el modal con un aviso de solape en pantalla deja el
    // timer vivo y su callback toca un DOM que ya no existe.
    if (this._avisoErrorTimer) clearTimeout(this._avisoErrorTimer);
    this._avisoErrorTimer = null;
    this._wmsPadres?.dispose();
    this._wmsPadres = null;
    this._padresWfs = null;
    this._searchBar.dispose();
    this._zoomIn.dispose();
    this._zoomOut.dispose();
    this._drawToolbar.dispose();
    this._map?.remove();
    this._map = null;
  }

  private _disposed = false;
  private _searchWrapEl: HTMLElement | null = null;
  private _avisoEl: HTMLElement | null = null;
  /** Fila de avisos ya pintada, para no repintar de gusto. Se compara el HTML
   *  entero y no solo el texto de la herramienta porque el error también
   *  cambia la fila. `undefined` = todavía no se pintó nada: el PRIMER pintado
   *  tiene que pasar aunque no haya nada activo, porque el card con el nombre
   *  del paso va desde el arranque. */
  private _avisoHtml: string | undefined = undefined;
  /** Error de solape en curso, o `null`. Lo pone `_onSolapeBloqueado` y lo
   *  saca su propio timer. */
  private _avisoError: string | null = null;
  private _avisoErrorTimer: ReturnType<typeof setTimeout> | null = null;
  private _hasShapes = false;
  private _herramientaActiva: AoiHerramienta | null = null;
  private _externalAvailabilityHandler: ((hasShapes: boolean) => void) | null =
    null;

  /** Cambió la cantidad de polígonos dibujados. Además de reenviarlo a quien
   *  lo haya pedido (`RegistroFincaPaso3Render`, que habilita "Continuar"),
   *  acá decide si el buscador sigue teniendo sentido. */
  private _onDrawAvailabilityChange = (hasShapes: boolean): void => {
    this._hasShapes = hasShapes;
    this._refreshBuscadorYAviso();
    this._externalAvailabilityHandler?.(hasShapes);
  };

  /** Cambió la herramienta activa de la toolbar (`AoiDrawToolbar.setOnToolChange`).
   *  Reemplaza a los eventos `L.Draw.Event.DRAWSTART`/`DRAWSTOP` que se
   *  escuchaban antes — se fueron con leaflet-draw. */
  private _onToolChange = (tool: AoiHerramienta | null): void => {
    this._herramientaActiva = tool;
    this._refreshBuscadorYAviso();
  };

  /** Visibilidad del buscador (enlace de Google Maps + Departamento/Municipio)
   *  y contenido de la fila de avisos. Los dos viven en `.aoi-map-topbar`, uno
   *  debajo del otro — ya NO comparten el mismo hueco, así que el aviso del
   *  paso puede quedarse visible mientras el buscador se oculta.
   *
   *  El buscador sirve para LLEGAR a la zona donde está el predio; una vez que
   *  el usuario está dibujando, o ya tiene algo dibujado, solo estorba y tapa
   *  el mapa. De ahí un único predicado que cubre los cuatro casos pedidos:
   *
   *    activa dibujar          → oculto  (herramienta === "draw")
   *    tiene ≥1 polígono       → oculto  (hasShapes)
   *    suelta dibujar sin nada → visible
   *    borra y queda en 0      → visible (borrar NO es "draw")
   *
   *  También aplica al restaurar (volver del paso 4 con polígonos ya
   *  dibujados: oculto desde el arranque) y es no-op en modo KML, donde el
   *  buscador nunca se crea (`_searchWrapEl` queda en `null`). */
  private _refreshBuscadorYAviso(): void {
    if (this._searchWrapEl) {
      const visible = !this._hasShapes && this._herramientaActiva !== "draw";
      this._searchWrapEl.hidden = !visible;
    }

    if (!this._avisoEl) return;
    const texto =
      this._herramientaActiva === "edit" &&
      !this._drawToolbar.esperandoPiezaParaEditar()
        ? AVISO_EDITAR_VERTICES
        : this._herramientaActiva
          ? AVISOS[this._herramientaActiva]
          : null;
    // Dos cards en la misma fila (`fx_filaAvisos`, compartido con el paso 4):
    // el del PASO va siempre, tenga o no herramienta activa (pedido explícito:
    // es lo que le dice al usuario qué tiene que hacer); al lado va la ayuda
    // de la herramienta, o el error de solape si hay uno en curso.
    const html = fx_filaAvisos(
      { texto: this._avisoPaso, icono: ICONO_PASO },
      texto,
      this._avisoError,
    );
    // Solo se repinta si el contenido cambió: `_refreshBuscadorYAviso` corre
    // también en cada alta/baja de polígono.
    if (html === this._avisoHtml) return;
    this._avisoHtml = html;
    // La fila se oculta solo si no quedó ningún card.
    this._avisoEl.hidden = html === "";
    this._avisoEl.innerHTML = html;
  }

  /** El motor rechazó algo por pisar otra pieza: se muestra el porqué y se
   *  borra solo. Cada rechazo REINICIA el reloj en vez de encolar otro aviso:
   *  dando clicks seguidos contra la misma pieza, el mensaje se mantiene hasta
   *  que el usuario para, y desaparece una sola vez. */
  /** `motivo` viene cuando el rechazo sabe explicarse solo — los de la edición
   *  de vértices lo traen. Sin él se usa el texto general, que es el caso del
   *  dibujo (ver `AoiDrawToolbar.setOnSolapeBloqueado`). */
  private _onSolapeBloqueado = (motivo?: string): void => {
    this._avisoError = motivo ?? AVISO_SOLAPE;
    this._refreshBuscadorYAviso();

    if (this._avisoErrorTimer) clearTimeout(this._avisoErrorTimer);
    this._avisoErrorTimer = setTimeout(() => {
      this._avisoErrorTimer = null;
      this._avisoError = null;
      this._refreshBuscadorYAviso();
    }, ERROR_TIMEOUT_MS);
  };

  /** Dibuja los 33 departamentos (contorno + label centrado) y limita el mapa
   *  a su extent conjunto con `fx_acotarMapaA` — el mismo criterio que
   *  `BaseMapRender._fitToLotes()`: el límite de paneo es lo que abarca la
   *  PANTALLA un nivel de zoom más atrás, no el extent inflado con `pad`. */
  private _drawDepartamentos(departamentos: StoredDepartamento[]): void {
    if (!this._map) return;

    const departamentosGroup = L.featureGroup();
    departamentos.forEach((departamento) => {
      // Sin tooltip de nombre (pedido explícito): el mapa base híbrido de
      // Google ya rotula departamentos y municipios, un label propio encima
      // duplicaba el texto. De paso resuelve el pendiente de rendimiento que
      // anotaba el CLAUDE.md — cada tooltip `permanent: true` era un `<div>`
      // posicionado, y un departamento con ~125 municipios los reposicionaba
      // TODOS en cada movimiento del mapa.
      const layer = L.geoJSON(departamento.areaGeografica, {
        pane: DEPARTAMENTOS_PANE,
        style: DEPARTAMENTO_STYLE,
      });
      layer.addTo(departamentosGroup);
      this._departamentoLayers.set(departamento.id, layer);
    });
    departamentosGroup.addTo(this._map);

    // Fade-in único (carga inicial): el pane arranca en opacity 0 (seteado
    // en connect()); requestAnimationFrame para que el navegador registre
    // ese estado antes de animar a 1 (si se setea sincrónico no anima).
    const departamentosPane = this._map.getPane(DEPARTAMENTOS_PANE);
    if (departamentosPane) {
      requestAnimationFrame(() => {
        departamentosPane.style.opacity = "1";
      });
    }

    const limites = fx_acotarMapaA(this._map, departamentosGroup.getBounds());
    if (limites) this._paisMaxBounds = limites.panBounds;
  }

  /** Callback del buscador (`AoiSearchBarRender`): `null` = deseleccionó el
   *  departamento (vuelve a la vista país); con id = encuadra ese
   *  departamento, acota el mapa a su extent, quita su tooltip y dibuja
   *  todos sus municipios. */
  private _selectDepartamento(departamentoId: string | null): void {
    if (!this._map) return;

    // Sin re-estilado: los contornos son amarillo fijo (ver DEPARTAMENTO_STYLE).
    // `_selectedDepartamentoId` se sigue guardando, pero solo como guard de la
    // carga asíncrona de municipios, no para pintar nada.
    this._selectedDepartamentoId = departamentoId;
    this._municipioLayerGroup.clearLayers();

    if (!departamentoId) {
      // maxBounds/minZoom NUNCA se tocan acá — quedan fijos a nivel país
      // (seteados una sola vez en _drawDepartamentos): solo se centra y hace
      // zoom de vuelta a ver todo el país.
      if (this._paisMaxBounds) {
        this._map.fitBounds(this._paisMaxBounds, { animate: true });
      }
      return;
    }

    const layer = this._departamentoLayers.get(departamentoId);
    if (!layer) return;

    this._map.fitBounds(layer.getBounds(), { animate: true });

    // Pide los municipios del departamento recién elegido (gRPC, por
    // departamentoId — mismo departamento ya visto en esta instancia resuelve
    // desde el cache en memoria del servicio, sin red). El guard evita pintar
    // municipios viejos si el usuario ya cambió de departamento antes de que
    // llegue la respuesta.
    municipioByDepartamentoIdGrpcService
      .getListAsync(departamentoId)
      .then((municipios: StoredMunicipio[]) => {
        if (this._selectedDepartamentoId !== departamentoId) return;

        // Crear varios L.geoJSON de golpe (geometrías reales DANE) es trabajo
        // síncrono pesado — en un frame aparte para no competir con el
        // fitBounds recién disparado y que la animación no se sienta trabada.
        requestAnimationFrame(() => {
          if (this._selectedDepartamentoId !== departamentoId) return;

          // Sin tooltip de nombre, misma razón que los departamentos.
          municipios.forEach((municipio) => {
            L.geoJSON(municipio.areaGeografica, {
              style: MUNICIPIO_STYLE,
            }).addTo(this._municipioLayerGroup);
          });
        });
      })
      .catch((error: unknown) => {
        console.warn("[AoiMapRender] No se pudieron cargar los municipios del departamento:", error);
      });
  }
}
